import {createAction, createSlice} from "@reduxjs/toolkit";
import {
	CHANGE_PASSWORD_FAILED,
	CHANGE_PASSWORD_FETCH,
	DELETE_USER_WITH_ID_DONE,
	DELETE_USER_WITH_ID_FAILED,
	DELETE_USER_WITH_ID_INIT,
	GET_LICENSE_TYPES_DONE,
	GET_LICENSE_TYPES_FAIL,
	GET_LICENSE_TYPES_INIT,
	GET_USER_LOCATION_DONE,
	GET_USER_LOCATION_FAIL,
	GET_USER_LOCATION_INIT,
	GET_USER_NAME_FAILED,
	GET_USER_NAME_FETCH,
	GET_USER_NAME_SUCCESS,
	LOAD_PROFILE_DONE,
	LOAD_PROFILE_FAIL,
	LOAD_PROFILE_INIT,
	LOGIN_FAILED,
	LOGIN_FETCH,
	LOGIN_SUCCESS,
	LOGOUT,
	REGISTER_CONTACTS_BLOCKED_BY_COUNTRY_DONE,
	REGISTER_CONTACTS_BLOCKED_BY_COUNTRY_FAIL,
	REGISTER_CONTACTS_BLOCKED_BY_COUNTRY_INIT,
	SEND_VERIFICATION_EMAIL_DONE,
	SEND_VERIFICATION_EMAIL_FAIL,
	SEND_VERIFICATION_EMAIL_INIT,
	TOGGLE_TUTORIAL_MODAL_OPEN,
	UPDATE_USER_CREDIT_INFORMATION,
	USERS_TRANSFER_DONE,
	USERS_TRANSFER_FAILED,
	USERS_TRANSFER_INIT,
	USERS_UPDATE_DONE,
	USERS_UPDATE_FAIL,
	USERS_UPDATE_INIT,
	VERIFY_EMAIL_DONE,
	VERIFY_EMAIL_FAIL,
	VERIFY_EMAIL_INIT,
	VERIFY_VERIFICATION_DONE,
	VERIFY_VERIFICATION_FAIL,
	VERIFY_VERIFICATION_INIT,
} from "store/mapXActionTypes";
import {RTUser, TUserReducerState} from "store/user/types";

export const userInitialState: TUserReducerState = {
	isTutorialModalOpened: false,
	loginInProgress: null,
	loggedOutManually: false,
	authUserError: false,
	user: null,
	getUserNameInProgress: null,
	getUserNameError: null,
	resetPasswordInProgress: null,
	deleteUserInProgress: false,
	userTransferProgress: false,
	resetPassProgress: false,
	resetPasswordError: null,
	iseEditedUser: false,
	verificationCodeInProgress: false,
	verificationCode: null,
	verificationEmailInProgress: false,
	verificationEmailError: null,
	verificationEmailSent: false,
	verificationEmail: "",
	updatingUsers: [],
	loadingProfile: false,
	registerContactsBlockedByCountryInProgress: false,
	registerContactsBlockedByCountrySubmitted: false,
	location: {},
	locationInProgress: false,
};

// Action creators — same names and signatures as userActions.js for drop-in import replacement
export const loginFetch = createAction(LOGIN_FETCH);
export const loginSuccess = createAction<RTUser>(LOGIN_SUCCESS);
export const loginFailed = createAction<unknown>(LOGIN_FAILED);
export const logout = createAction(LOGOUT);

export const loadProfileInit = createAction(LOAD_PROFILE_INIT);
export const loadProfileDone = createAction<RTUser>(LOAD_PROFILE_DONE);
export const loadProfileFail = createAction<unknown>(LOAD_PROFILE_FAIL);

export const getUserNameFetch = createAction(GET_USER_NAME_FETCH);
export const getUserNameSuccess = createAction<string>(GET_USER_NAME_SUCCESS);
export const getUserNameFailed = createAction<unknown>(GET_USER_NAME_FAILED);

// License type actions belong to the company reducer but were dispatched from user async actions
export const getLicenseTypesInit = createAction(GET_LICENSE_TYPES_INIT);
export const getLicenseTypeDone = createAction<unknown>(GET_LICENSE_TYPES_DONE);
export const getLicenseTypesFailed = createAction<unknown>(GET_LICENSE_TYPES_FAIL);

export const changePasswordFetch = createAction<unknown>(CHANGE_PASSWORD_FETCH);
export const changePasswordFailed = createAction<unknown>(CHANGE_PASSWORD_FAILED);

type TUserCreditPayload = {
	credits: number;
	license: RTUser["license"];
	credit_refresh: string | null;
};

export const setUserCreditInformation = createAction<TUserCreditPayload>(
	UPDATE_USER_CREDIT_INFORMATION,
);
export const updateToggleModalHandler = createAction(TOGGLE_TUTORIAL_MODAL_OPEN);

export const deleteUserWithIdInit = createAction(DELETE_USER_WITH_ID_INIT);
export const deleteUserWithIdDone = createAction<unknown>(DELETE_USER_WITH_ID_DONE);
export const deleteUserWithIdFailed = createAction<unknown>(DELETE_USER_WITH_ID_FAILED);

export const usersUpdateInit = createAction<number>(USERS_UPDATE_INIT);
export const usersUpdateDone = createAction(USERS_UPDATE_DONE, (data: unknown, userID: number) => ({
	payload: userID,
	data,
}));
export const usersUpdateFailed = createAction(
	USERS_UPDATE_FAIL,
	(_error: unknown, userID: number) => ({payload: userID}),
);

export const usersTransferInit = createAction(USERS_TRANSFER_INIT);
export const usersTransferDone = createAction<unknown>(USERS_TRANSFER_DONE);
export const usersTransferFailed = createAction(USERS_TRANSFER_FAILED);

export const verifyEmailInit = createAction(VERIFY_EMAIL_INIT);
export const verifyEmailDone = createAction(VERIFY_EMAIL_DONE);
export const verifyEmailFail = createAction(VERIFY_EMAIL_FAIL);

export const verifyVerificationInit = createAction(VERIFY_VERIFICATION_INIT);
export const verifyVerificationDone = createAction<string>(VERIFY_VERIFICATION_DONE);
export const verifyVerificationFail = createAction<unknown>(VERIFY_VERIFICATION_FAIL);

export const sendVerificationEmailInit = createAction<string>(SEND_VERIFICATION_EMAIL_INIT);
export const sendVerificationEmailDone = createAction<string>(SEND_VERIFICATION_EMAIL_DONE);
export const sendVerificationEmailFail = createAction<unknown>(SEND_VERIFICATION_EMAIL_FAIL);

export const registerContactsBlockedByCountryInit = createAction(
	REGISTER_CONTACTS_BLOCKED_BY_COUNTRY_INIT,
);
export const registerContactsBlockedByCountryDone = createAction(
	REGISTER_CONTACTS_BLOCKED_BY_COUNTRY_DONE,
);
export const registerContactsBlockedByCountryFail = createAction<unknown>(
	REGISTER_CONTACTS_BLOCKED_BY_COUNTRY_FAIL,
);

export const getUserLocationInit = createAction(GET_USER_LOCATION_INIT);
export const getUserLocationDone = createAction<unknown>(GET_USER_LOCATION_DONE);
export const getUserLocationFail = createAction<unknown>(GET_USER_LOCATION_FAIL);

const userSlice = createSlice({
	name: "user",
	initialState: userInitialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(loginFetch, (state) => {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				(state as any).authAdminInProgress = true;
			})
			.addCase(loginSuccess, (state, action) => {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				(state as any).authAdminInProgress = false;
				state.user = action.payload;
				state.loggedOutManually = false;
			})
			.addCase(loginFailed, (state, action) => {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				(state as any).authAdminError = action.payload;
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				(state as any).authAdminInProgress = false;
			})
			.addCase(logout, () => ({...userInitialState, loggedOutManually: true}))
			.addCase(loadProfileInit, (state) => {
				state.loadingProfile = true;
			})
			.addCase(loadProfileDone, (state, action) => {
				state.loadingProfile = false;
				state.user = action.payload;
			})
			.addCase(loadProfileFail, (state) => {
				state.loadingProfile = false;
			})
			.addCase(getUserNameFetch, (state) => {
				state.getUserNameInProgress = true;
			})
			.addCase(getUserNameSuccess, (state, action) => {
				state.getUserNameInProgress = false;
				if (state.user) {
					state.user.email = action.payload;
				}
			})
			.addCase(getUserNameFailed, (state, action) => {
				state.getUserNameError = action.payload as string;
				state.getUserNameInProgress = false;
			})
			.addCase(changePasswordFetch, (state, action) => {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				(state as any).changePass = action.payload;
			})
			.addCase(changePasswordFailed, (state, action) => {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				(state as any).changePassFailed = action.payload;
			})
			.addCase(setUserCreditInformation, (state, action) => {
				if (state.user) {
					state.user = {...state.user, ...action.payload};
				}
			})
			.addCase(updateToggleModalHandler, (state) => {
				state.isTutorialModalOpened = !state.isTutorialModalOpened;
			})
			.addCase(deleteUserWithIdInit, (state) => {
				state.deleteUserInProgress = true;
			})
			.addCase(deleteUserWithIdDone, (state) => {
				state.deleteUserInProgress = false;
			})
			.addCase(deleteUserWithIdFailed, (state) => {
				state.deleteUserInProgress = false;
			})
			.addCase(usersUpdateInit, (state, action) => {
				state.iseEditedUser = true;
				state.updatingUsers = [...state.updatingUsers, action.payload];
			})
			.addCase(usersUpdateDone, (state, action) => {
				state.iseEditedUser = false;
				state.updatingUsers = state.updatingUsers.filter((id) => id !== action.payload);
			})
			.addCase(usersUpdateFailed, (state, action) => {
				state.iseEditedUser = false;
				state.updatingUsers = state.updatingUsers.filter((id) => id !== action.payload);
			})
			.addCase(usersTransferInit, (state) => {
				state.userTransferProgress = true;
			})
			.addCase(usersTransferDone, (state) => {
				state.userTransferProgress = false;
			})
			.addCase(usersTransferFailed, (state) => {
				state.userTransferProgress = false;
			})
			.addCase(verifyEmailInit, (state) => {
				state.resetPassProgress = true;
			})
			.addCase(verifyEmailDone, (state) => {
				state.resetPassProgress = false;
			})
			.addCase(verifyEmailFail, (state) => {
				state.resetPassProgress = false;
			})
			.addCase(verifyVerificationInit, (state) => {
				state.verificationCodeInProgress = true;
			})
			.addCase(verifyVerificationDone, (state, action) => {
				state.verificationCode = action.payload;
				state.verificationCodeInProgress = false;
			})
			.addCase(verifyVerificationFail, (state) => {
				state.verificationCodeInProgress = false;
			})
			.addCase(sendVerificationEmailInit, (state) => {
				state.verificationEmailInProgress = true;
				state.verificationEmailError = null;
				state.verificationEmailSent = false;
			})
			.addCase(sendVerificationEmailDone, (state, action) => {
				state.verificationEmailInProgress = false;
				state.verificationEmailSent = true;
				state.verificationEmail = action.payload;
			})
			.addCase(sendVerificationEmailFail, (state, action) => {
				state.verificationEmailInProgress = false;
				state.verificationEmailError = action.payload as string;
			})
			.addCase(registerContactsBlockedByCountryInit, (state) => {
				state.registerContactsBlockedByCountryInProgress = true;
			})
			.addCase(registerContactsBlockedByCountryDone, (state) => {
				state.registerContactsBlockedByCountryInProgress = false;
				state.registerContactsBlockedByCountrySubmitted = true;
			})
			.addCase(registerContactsBlockedByCountryFail, (state) => {
				state.registerContactsBlockedByCountryInProgress = false;
			})
			.addCase(getUserLocationInit, (state) => {
				state.locationInProgress = true;
			})
			.addCase(getUserLocationDone, (state, action) => {
				state.locationInProgress = false;
				state.location = action.payload as NonNullable<unknown>;
			})
			.addCase(getUserLocationFail, (state) => {
				state.locationInProgress = false;
			});
	},
});

export default userSlice.reducer;
